import React, { useContext, useEffect } from "react";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { SettingsCardContext, SettingsSectionContext, useSettingsPage } from "./SettingsPageContext";

type Props = {
    /** DOM id and registry id, e.g. "orders-rules". */
    id: string;
    titleKey: string;
    descriptionKey?: string;
    /**
     * Rows in this card save on their own (theme, sidebars, production workflow) instead of
     * through the page's Save. Shows a label in the header so nobody waits on Save for them.
     */
    savesImmediately?: boolean;
    /** Extra content on the right of the header, e.g. an integration chip. */
    headerRight?: React.ReactNode;
    children?: React.ReactNode;
};

/**
 * A card groups related rows under a short title. Registers itself for the sticky header's
 * sub-section menu and scroll-spy.
 */
export const SettingsCard = (props: Props) => {
    const page = useSettingsPage();
    const section = useContext(SettingsSectionContext);
    const { registerCard } = page;
    const sectionId = section ? section.id : "";

    useEffect(() => {
        return registerCard({ id: props.id, sectionId, titleKey: props.titleKey });
    }, [registerCard, props.id, sectionId, props.titleKey]);

    const cardContextValue = React.useMemo(() => ({ id: props.id, titleKey: props.titleKey }), [props.id, props.titleKey]);

    return (
        <SettingsCardContext.Provider value={cardContextValue}>
            <div className={"SettingsCard"} id={props.id} data-testid={`card-${props.id}`}>
                <div className={"SettingsCard-head"}>
                    <div>
                        <h3 className={"SettingsCard-title"}>
                            <OwLabel title={props.titleKey} noBottomMargin={true} />
                        </h3>
                        {props.descriptionKey ? (
                            <p className={"SettingsCard-description"}>
                                <OwLabel title={props.descriptionKey} noBottomMargin={true} />
                            </p>
                        ) : null}
                    </div>
                    <div className={"SettingsCard-headRight"}>
                        {props.headerRight}
                        {props.savesImmediately ? (
                            <span className={"SettingsCard-savesImmediately"}>
                                <i className={"fad fa-bolt"} aria-hidden={true} />
                                <OwLabel title={"saves_immediately"} noBottomMargin={true} />
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className={"SettingsCard-body"}>{props.children}</div>
            </div>
        </SettingsCardContext.Provider>
    );
};

export default SettingsCard;
